import { useState } from 'react';
import { Box, Button, Grid, Paper, TextField, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import type { TransferFlowState } from '../flowConfig';
import type { TransferLocation } from '../../../api/types/transfer';
import type { CorporateEmployee } from '../../../api/types/corporateEmployee';
import { LocationAutocomplete } from './LocationAutocomplete';
import { PersonnelAutocomplete } from '../../../components/PersonnelAutocomplete';

export function TransferSearchStep() {
  const flow = useBookingFlowContext<TransferFlowState>();
  const [pickup, setPickup] = useState<TransferLocation | null>(null);
  const [dropoff, setDropoff] = useState<TransferLocation | null>(null);
  const [dateTime, setDateTime] = useState<Dayjs | null>(dayjs());
  const [adtCount, setAdtCount] = useState(1);
  const [chdCount, setChdCount] = useState(0);
  const [infCount, setInfCount] = useState(0);
  const [employee, setEmployee] = useState<CorporateEmployee | null>(null);

  const canSearch = !!pickup && !!dropoff && !!dateTime;

  const handleSearch = () => {
    if (!pickup || !dropoff || !dateTime) return;
    flow.runStep({
      searchRequest: {
        pickupPointId: pickup.locId,
        dropPointId: dropoff.locId,
        pickupPoint: pickup.locName,
        dropPoint: dropoff.locName,
        transferDateAndTime: dateTime.format('YYYY-MM-DDTHH:mm:ss'),
        adtPaxCount: adtCount,
        chdPaxCount: chdCount,
        infPaxCount: infCount,
        employeeID: employee?.employeeID,
      },
    });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Transfer Arama
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <LocationAutocomplete label="Nereden" value={pickup} onChange={setPickup} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LocationAutocomplete label="Nereye" value={dropoff} onChange={setDropoff} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimePicker
            label="Transfer Tarih ve Saati"
            value={dateTime}
            onChange={(newValue) => setDateTime(newValue)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PersonnelAutocomplete value={employee} onChange={setEmployee} />
        </Grid>
        <Grid size={{ xs: 4, md: 2 }}>
          <TextField
            label="Yetişkin"
            type="number"
            size="small"
            fullWidth
            value={adtCount}
            onChange={(e) => setAdtCount(Math.max(1, Number(e.target.value)))}
            slotProps={{ htmlInput: { min: 1 } }}
          />
        </Grid>
        <Grid size={{ xs: 4, md: 2 }}>
          <TextField
            label="Çocuk"
            type="number"
            size="small"
            fullWidth
            value={chdCount}
            onChange={(e) => setChdCount(Math.max(0, Number(e.target.value)))}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </Grid>
        <Grid size={{ xs: 4, md: 2 }}>
          <TextField
            label="Bebek"
            type="number"
            size="small"
            fullWidth
            value={infCount}
            onChange={(e) => setInfCount(Math.max(0, Number(e.target.value)))}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" disabled={!canSearch || flow.status === 'running'} onClick={handleSearch}>
          {flow.status === 'running' ? 'Aranıyor…' : 'Ara'}
        </Button>
      </Box>
    </Paper>
  );
}
